import React, { useState, useEffect } from 'react';

const VoicePlayer = ({ voiceUrl, uploadId, userId, compact = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio, setAudio] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    if (voiceUrl) {
      const audioElement = new Audio(voiceUrl);
      audioElement.addEventListener('loadedmetadata', () => {
        setDuration(audioElement.duration);
      });
      audioElement.addEventListener('timeupdate', () => {
        setCurrentTime(audioElement.currentTime);
      });
      audioElement.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
      setAudio(audioElement);
    }
  }, [voiceUrl]);

  const togglePlay = () => {
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const seekTime = (clickX / width) * duration;
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  if (!voiceUrl) return null;

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const btnSize = compact ? 28 : 32;
  const btnFont = compact ? 12 : 14;

  if (compact) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'nowrap',
          padding: '4px 8px',
          background: 'var(--branding-app-surface-panel)',
          borderRadius: 8,
          border: '1px solid var(--branding-app-surface-panel-border)',
          marginTop: 0,
          minWidth: 200,
          maxWidth: 320,
          flexShrink: 0,
        }}
        title="Voice note"
      >
        <button
          type="button"
          onClick={togglePlay}
          style={{
            background: isHovering ? 'var(--branding-app-primary-hover)' : 'var(--branding-app-primary)',
            color: 'var(--branding-app-primary-fg)',
            border: 'none',
            borderRadius: '50%',
            width: btnSize,
            height: btnSize,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: btnFont,
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <div style={{ flex: '1 1 72px', minWidth: 72, maxWidth: 160 }}>
          <div
            onClick={handleSeek}
            role="slider"
            aria-valuenow={Math.round(currentTime)}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, Math.round(duration))}
            style={{
              width: '100%',
              height: 4,
              background: 'var(--branding-app-surface-panel-border)',
              borderRadius: 2,
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                background: 'var(--branding-app-primary)',
                borderRadius: 2,
                transition: 'width 0.1s',
              }}
            />
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            color: 'var(--cc-text-muted)',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
            minWidth: '4.5em',
            textAlign: 'right',
          }}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      background: 'var(--branding-app-surface-panel)',
      borderRadius: 8,
      border: '1px solid var(--branding-app-surface-panel-border)',
      marginTop: 8
    }}>
      <button
        type="button"
        onClick={togglePlay}
        style={{
          background: isHovering ? 'var(--branding-app-primary-hover)' : 'var(--branding-app-primary)',
          color: 'var(--branding-app-primary-fg)',
          border: 'none',
          borderRadius: '50%',
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: 14,
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {isPlaying ? '⏸' : '▶'}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          onClick={handleSeek}
          style={{
            width: '100%',
            height: 4,
            background: 'var(--branding-app-surface-panel-border)',
            borderRadius: 2,
            cursor: 'pointer',
            position: 'relative',
            marginBottom: 4
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: 'var(--branding-app-primary)',
              borderRadius: 2,
              transition: 'width 0.1s'
            }}
          />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 12,
          color: 'var(--cc-text-muted)',
          fontVariantNumeric: 'tabular-nums',
          gap: 12,
        }}>
          <span style={{ flexShrink: 0 }}>{formatTime(currentTime)}</span>
          <span style={{ flexShrink: 0 }}>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default VoicePlayer;
